import React from "react";
import styled from "styled-components";
import GridCardComponent from "./generic/GridCardComponent";
import { defaultStatusColors, getSpanWithColor } from "../../../utils/CardUtils";

/* #region  SCOPED CSS */
const StyledPage = styled.div``;
/* #endregion */

/**
 * Renders a grid card component for displaying brand information.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.className - The CSS class name for the component.
 * @param {string} props.title - The title of the brand.
 * @param {string} props.id - The ID of the brand.
 * @param {boolean} props.hasBorders - Indicates if the card has borders.
 * @param {string} [props.titlePrefix="BRAND"] - The prefix for the title.
 * @param {string} [props.idPrefix="BOTAG ID"] - The prefix for the ID.
 * @param {boolean} props.isCardEnabled - Indicates if the card is enabled.
 * @param {boolean} props.isCardSuspended - Indicates if the card is suspended.
 * @param {string} props.image - The image URL for the brand.
 * @param {Object[]} props.details - The details to display in the card.
 * @param {string} props.details[].key - The key of the detail.
 * @param {string} props.details[].value - The value of the detail.
 * @param {Object} props.edit - The edit button configuration.
 * @param {Object} props.suspend - The suspend button configuration.
 * @param {Object} props.delete - The delete button configuration.
 * @returns {JSX.Element} The rendered POSPageGridCard component.
 */
export default function POSPageGridCard(props) {
    const status = props?.data?.status ?? "--";

    return (
        <StyledPage className={`${props?.className || ""}`}>
            <GridCardComponent
                primaryButton={{
                    title: props?.primaryButton?.title || "--",
                    onClick: props?.primaryButton?.onClick
                }}
                secondaryButton={{
                    title: props?.secondaryButton?.title || "--",
                    onClick: props?.secondaryButton?.onClick
                }}
                title={props?.title}
                id={props?.id}
                hasBorders={props?.hasBorders}
                titlePrefix={props?.titlePrefix || "--"}
                idPrefix={props?.idPrefix || "--"}
                isCardEnabled={props?.isCardEnabled}
                isCardSuspended={props?.isCardSuspended}
                image={props?.image}
                imageHasPadding={true}
                isImageWithOverlay={false}
                imageOverlay={props?.imageOverlay}
                overLayBackgroundColor={props?.imageOverlay && "#10141B"}
                highlighted={props?.highlighted}
                highlightColor={props?.highlightColor}
                highlightCustomText={props?.highlightCustomText}
                //overLayBorderTop={props?.imageOverlay && "1px solid rgba(30, 38, 49, 1)"}
                details={[
                    { key: "Created by:", value: props?.data?.createdBy ?? "--" },
                    { key: "Created on:", value: props?.data?.createdOn ?? "--" },
                    { key: "Company:", value: props?.data?.companyName ?? "--" },
                    { key: "Support Email:", value: props?.data?.supportEmail ?? "--" },
                    {
                        key: "Products Import:",
                        value: getSpanWithColor(props?.data?.productsImport ?? "--", defaultStatusColors)
                    },
                    {
                        key: "Supplier Orders Import:",
                        value: getSpanWithColor(props?.data?.supplierOrdersImport ?? "--", defaultStatusColors)
                    },
                    {
                        key: "Products Export:",
                        value: getSpanWithColor(props?.data?.productsExport ?? "--", defaultStatusColors)
                    },
                    {
                        key: "Supplier Orders Export:",
                        value: getSpanWithColor(props?.data?.supplierOrdersExport ?? "--", defaultStatusColors)
                    },
                    {
                        key: "Stock Import:",
                        value: getSpanWithColor(props?.data?.stockImport ?? "--", defaultStatusColors)
                    },
                    {
                        key: "Prices Import:",
                        value: getSpanWithColor(props?.data?.pricesImport ?? "--", defaultStatusColors)
                    },
                    {
                        key: "Customer Orders:",
                        value: getSpanWithColor(props?.data?.customerOrders ?? "--", defaultStatusColors)
                    },
                    { key: "Status:", value: getSpanWithColor(status ?? "--", defaultStatusColors) }
                ]}
                edit={{ hidden: true, ...props?.edit }}
                suspend={props?.suspend}
                delete={props?.delete}
                noOfRows={6}
            />
        </StyledPage>
    );
}
